import React, { useMemo } from "react";
import { COLORS } from "../../../../theme/colors";
import EditableField from "./EditableField";
import { useAvailableModels } from "../../../../hooks/useAvailableModels";

// agent-experience-redesign-v1.md §5 + §7 — per-node drawer that
// renders editable fields appropriate to the LangGraph node clicked.
// Mounted by AgentViewer when drawer.payload carries a `node` shape
// (set by GraphTab.jsx).
//
// Fields rendered per node kind:
//   call_llm                → system_prompt, provider, model,
//                             temperature, max_tokens, advanced
//                             (top_p / freq / presence), smart_routing
//   check_input_guardrails  → on/off, blocked_topics, pii_detection
//   check_output_guardrails → on/off, hallucination_check, compliance
//   fact_check              → level, confidence_threshold,
//                             verify_numbers, require_sources
//   execute_tools           → toolsets opt-in checklist
//   parallel_prepare        → memory.long_term_enabled, memory.window_size
//   process_attachments     → attachment_processing config (read-only)
//   detect_language         → language_detection config (read-only)
//   others                  → display config_slice as JSON for now
//
// All fields gate on canEdit (RBAC §5.5: business_user → view-only).
// Saves go through `saveAgent` from AgentViewer (412 conflict + tree
// refetch handled there).

const SECTION_TITLE = {
  call_llm:                "LLM Engine",
  check_input_guardrails:  "Input Guardrails",
  check_output_guardrails: "Output Guardrails",
  fact_check:              "Fact Checker",
  execute_tools:            "Tools",
  parallel_prepare:        "Memory + RAG",
  process_attachments:     "Attachments",
  detect_language:         "Language Detection",
  orchestrate_workflow:    "Workflow Orchestration",
  aggregate_results:       "Result Aggregation",
  no_agent_fallback:       "Fallback",
  route_agent:             "Router",
  check_escalation:        "Escalation",
};

// Smart-routing tier slots — three rows × (provider, model). Per
// CLAUDE.md "Smart LLM Routing" the tiers are fast / standard / complex.
const SMART_TIERS = [
  { id: "fast",     label: "Fast",     desc: "Greetings, classification, low complexity" },
  { id: "standard", label: "Standard", desc: "Normal customer turns" },
  { id: "complex",  label: "Complex",  desc: "Multi-step reasoning, code, analysis" },
];

const FACT_CHECK_LEVELS = [
  { value: "off",      label: "Off"      },
  { value: "standard", label: "Standard" },
  { value: "strict",   label: "Strict"   },
];

// Toolsets registered in the platform — derived from the 9 toolsets in
// CLAUDE.md (analysis / approvals / communication / demo / query plus
// the role-bound builder/admin/tester/analytics already auto-injected).
const KNOWN_TOOLSETS = [
  { name: "approvals",     hint: "Human-in-the-loop approval requests" },
  { name: "communication", hint: "send_email + send_sms"               },
  { name: "analysis",      hint: "Map-reduce document analysis"        },
  { name: "query",         hint: "Smart query (SQL/Graph/Vector)"      },
  { name: "demo",          hint: "Mock CRM + JIRA toolset"             },
];

export default function GraphNodeDrawer({ node, agent, saveAgent, canEdit }) {
  const fc  = agent?.flow_config || {};
  const meta = agent?.metadata_json || {};
  const kind = node?.id;
  const title = SECTION_TITLE[kind] || node?.label || kind || "Node";

  // ── Save adapters (each writes the minimal partial-patch the
  // server deep-merges back into the row) ─────────────────────────
  const saveProvider     = async (v) => saveAgent({ provider: v });
  const saveModel        = async (v) => saveAgent({ model: v });
  const saveSystemPrompt = async (v) => saveAgent({ system_prompt: v });
  const saveTemperature  = async (v) => saveAgent({ temperature: Number(v) });
  const saveMaxTokens    = async (v) => saveAgent({ max_tokens: Number(v) });
  const saveLLMField     = async (key, v) => saveAgent({ flow_config: { llm: { [key]: v } } });
  const saveFactChecker  = async (key, v) => saveAgent({ flow_config: { fact_checker: { [key]: v } } });
  const saveSmartTier    = async (tierId, key, v) => saveAgent({
    flow_config: { llm: { smart_routing: { [tierId]: { [key]: v } } } },
  });
  const saveToolsets     = async (newList) => saveAgent({
    flow_config: { api: { internal_toolsets: newList } },
  });
  const saveMemory       = async (key, v) => saveAgent({ flow_config: { memory: { [key]: v } } });
  const saveKnowledge    = async (key, v) => saveAgent({ flow_config: { knowledge: { [key]: v } } });
  const saveGuardField   = async (which, key, v) => saveAgent({
    flow_config: { [which]: { [key]: v } },
  });

  if (!node || !agent) {
    return <div style={{ color: COLORS.textDim, fontSize: 12 }}>No node selected.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
          {`Node id: ${node.id}`}
        </div>
      </div>

      {kind === "call_llm" && (
        <CallLlmFields
          agent={agent}
          fc={fc}
          canEdit={canEdit}
          saveProvider={saveProvider}
          saveModel={saveModel}
          saveSystemPrompt={saveSystemPrompt}
          saveTemperature={saveTemperature}
          saveMaxTokens={saveMaxTokens}
          saveLLMField={saveLLMField}
          saveSmartTier={saveSmartTier}
        />
      )}

      {kind === "fact_check" && (
        <FactCheckFields
          fc={fc}
          canEdit={canEdit}
          saveFactChecker={saveFactChecker}
        />
      )}

      {kind === "execute_tools" && (
        <ToolsetsFields
          fc={fc}
          canEdit={canEdit}
          saveToolsets={saveToolsets}
        />
      )}

      {kind === "check_input_guardrails" && (
        <GuardrailFields
          which="input_guardrails"
          fc={fc}
          canEdit={canEdit}
          saveGuardField={saveGuardField}
        />
      )}

      {kind === "check_output_guardrails" && (
        <GuardrailFields
          which="output_guardrails"
          fc={fc}
          canEdit={canEdit}
          saveGuardField={saveGuardField}
        />
      )}

      {kind === "parallel_prepare" && (
        <MemoryRagFields
          fc={fc}
          canEdit={canEdit}
          saveMemory={saveMemory}
          saveKnowledge={saveKnowledge}
        />
      )}

      {/* Catch-all for nodes without dedicated UI yet — show
          read-only config slice so the user still has visibility. */}
      {!["call_llm", "fact_check", "execute_tools",
         "check_input_guardrails", "check_output_guardrails",
         "parallel_prepare"].includes(kind) && (
        <ReadOnlyConfigSlice node={node} fc={fc} meta={meta} />
      )}
    </div>
  );
}

// RE: reasoning_effort is a gpt-5 / o-series capability. Mirror the backend's
// model_capabilities._NEW_PARAM_PREFIXES so the control shows ONLY for a model
// that accepts it (INV-CAP-GATED-UI). The FE has no capability map, so a
// family-prefix check is the pragmatic gate (same style as the kind==="chat"
// model filter elsewhere).
const REASONING_MODEL_PREFIXES = ["gpt-5", "o1", "o3", "o4"];
export function supportsReasoningEffort(model) {
  return !!model && REASONING_MODEL_PREFIXES.some((p) => model.startsWith(p));
}

// ── call_llm (B2 + B3 + B6) ─────────────────────────────────
function CallLlmFields({ agent, fc, canEdit, saveProvider, saveModel,
                        saveSystemPrompt, saveTemperature, saveMaxTokens,
                        saveLLMField, saveSmartTier }) {
  const { models, llmConfigured } = useAvailableModels();
  const llmCfg = fc.llm || {};
  const advanced = llmCfg.advanced || {};
  const smartRouting = llmCfg.smart_routing || {};

  const providerOptions = useMemo(() => {
    const keys = Object.keys(models || {});
    if (keys.length) return keys.map(k => ({ value: k, label: k }));
    // Fallback to the four known providers if no live data; user still
    // can't pick a model since the model dropdown is BYOK-driven.
    return [
      { value: "openai",    label: "openai"    },
      { value: "anthropic", label: "anthropic" },
      { value: "vertex",    label: "vertex"    },
      { value: "bedrock",   label: "bedrock"   },
    ];
  }, [models]);

  const modelOptions = useMemo(() => {
    const list = (models || {})[agent.provider] || [];
    return list.map(m => ({
      value: m.id || m.name,
      label: m.id || m.name,
    }));
  }, [models, agent.provider]);

  // RE — hoisted to a statement (not an inline JSX && in the return) so v8
  // line-coverage attributes it cleanly on CI's node-22. Shown only for a
  // model that accepts reasoning_effort (INV-CAP-GATED-UI); saving "" clears
  // the field so behavior is unchanged when unset.
  let reasoningEffortControl = null;
  if (supportsReasoningEffort(agent.model)) {
    reasoningEffortControl = (
      <EditableField
        label="Reasoning effort"
        variant="select"
        value={llmCfg.reasoning_effort || ""}
        options={[
          { value: "",        label: "(default — medium)" },
          { value: "minimal", label: "minimal — fastest" },
          { value: "low",     label: "low" },
          { value: "medium",  label: "medium" },
          { value: "high",    label: "high — deepest" },
        ]}
        onSave={(v) => saveLLMField("reasoning_effort", v || null)}
        readOnly={!canEdit}
        hint="gpt-5 / o-series only · lower = faster, less hidden reasoning"
      />
    );
  }

  return (
    <>
      <Section title="Provider + Model">
        <EditableField
          label="Provider"
          variant="select"
          value={agent.provider || ""}
          options={[{ value: "", label: "(not configured)" }, ...providerOptions]}
          onSave={saveProvider}
          readOnly={!canEdit}
        />
        <EditableField
          label="Model"
          variant="select"
          value={agent.model || ""}
          options={[{ value: "", label: "(pick provider first)" }, ...modelOptions]}
          onSave={saveModel}
          readOnly={!canEdit || !modelOptions.length}
          hint={!llmConfigured ? "No provider configured — set keys in Admin → API Keys" : undefined}
        />
      </Section>

      <Section title="System Prompt">
        <EditableField
          variant="textarea"
          rows={6}
          value={agent.system_prompt || ""}
          onSave={saveSystemPrompt}
          placeholder="System prompt that grounds this agent's behavior…"
          readOnly={!canEdit}
        />
      </Section>

      <Section title="Sampling">
        <EditableField
          label="Temperature"
          variant="slider"
          value={agent.temperature ?? 0.3}
          min={0} max={2} step={0.05}
          onSave={saveTemperature}
          readOnly={!canEdit}
          hint="0 = deterministic · 1 = creative · 2 = wild"
        />
        <EditableField
          label="Max tokens"
          variant="number"
          value={agent.max_tokens ?? 2048}
          min={64} max={32000}
          onSave={saveMaxTokens}
          readOnly={!canEdit}
        />
        {/* RE — reasoning_effort control (hoisted above; shown only for
            gpt-5 / o-series). */}
        {reasoningEffortControl}
        {/* Advanced — top_p / freq / presence (B3). All optional; null
            preserves provider defaults. */}
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: 11, color: COLORS.textMuted, fontWeight: 600 }}>
            Advanced sampling
          </summary>
          <div style={{ paddingTop: 8, paddingLeft: 4 }}>
            <EditableField
              label="top_p"
              variant="slider"
              value={advanced.top_p ?? 1}
              min={0} max={1} step={0.05}
              onSave={(v) => saveLLMField("advanced", { ...advanced, top_p: Number(v) })}
              readOnly={!canEdit}
            />
            <EditableField
              label="frequency_penalty"
              variant="slider"
              value={advanced.frequency_penalty ?? 0}
              min={-2} max={2} step={0.1}
              onSave={(v) => saveLLMField("advanced", { ...advanced, frequency_penalty: Number(v) })}
              readOnly={!canEdit}
            />
            <EditableField
              label="presence_penalty"
              variant="slider"
              value={advanced.presence_penalty ?? 0}
              min={-2} max={2} step={0.1}
              onSave={(v) => saveLLMField("advanced", { ...advanced, presence_penalty: Number(v) })}
              readOnly={!canEdit}
            />
          </div>
        </details>
      </Section>

      {/* B6 — smart-routing tiers. Each tier picks (provider, model)
          out of the same BYOK-driven options. Backend reads
          flow_config.llm.smart_routing[tier].provider/model. */}
      <Section title="Smart routing">
        <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8, lineHeight: 1.5 }}>
          Auto-classifies each turn and routes to a tier. Empty tier = use the agent's primary provider/model.
        </div>
        {SMART_TIERS.map((tier) => {
          const t = smartRouting[tier.id] || {};
          const tierProvider = t.provider || "";
          const tierModelOptions = (models?.[tierProvider] || []).map(m => ({
            value: m.id || m.name, label: m.id || m.name,
          }));
          return (
            <div key={tier.id} style={{
              padding: 10, borderRadius: 8,
              border: `1px solid ${COLORS.border}`, marginBottom: 8,
              background: COLORS.surface,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 2 }}>
                {tier.label}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 6 }}>{tier.desc}</div>
              <EditableField
                label="Provider"
                variant="select"
                value={tierProvider}
                options={[{ value: "", label: "(use primary)" }, ...providerOptions]}
                onSave={(v) => saveSmartTier(tier.id, "provider", v || null)}
                readOnly={!canEdit}
              />
              <EditableField
                label="Model"
                variant="select"
                value={t.model || ""}
                options={[{ value: "", label: "(use primary)" }, ...tierModelOptions]}
                onSave={(v) => saveSmartTier(tier.id, "model", v || null)}
                readOnly={!canEdit || !tierModelOptions.length}
              />
            </div>
          );
        })}
      </Section>
    </>
  );
}

// ── fact_check (B5) ─────────────────────────────────────────
function FactCheckFields({ fc, canEdit, saveFactChecker }) {
  const fcfg = fc.fact_checker || {};
  return (
    <Section title="Fact Checker">
      <EditableField
        label="Level"
        variant="pill"
        value={fcfg.level || "off"}
        options={FACT_CHECK_LEVELS}
        onSave={(v) => saveFactChecker("level", v)}
        readOnly={!canEdit}
        hint="Standard = fast-path only · Strict = fast + slow path verification"
      />
      <EditableField
        label="Confidence threshold"
        variant="slider"
        value={fcfg.confidence_threshold ?? 0.7}
        min={0} max={1} step={0.05}
        onSave={(v) => saveFactChecker("confidence_threshold", Number(v))}
        readOnly={!canEdit}
      />
      <EditableField
        label="Verify numbers"
        variant="pill"
        value={fcfg.verify_numbers ? "yes" : "no"}
        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
        onSave={(v) => saveFactChecker("verify_numbers", v === "yes")}
        readOnly={!canEdit}
      />
      <EditableField
        label="Require sources"
        variant="pill"
        value={fcfg.require_sources ? "yes" : "no"}
        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
        onSave={(v) => saveFactChecker("require_sources", v === "yes")}
        readOnly={!canEdit}
      />
    </Section>
  );
}

// ── execute_tools (B4) ──────────────────────────────────────
function ToolsetsFields({ fc, canEdit, saveToolsets }) {
  const apiCfg = fc.api || {};
  const enabled = Array.isArray(apiCfg.internal_toolsets) ? apiCfg.internal_toolsets : [];
  const toggle = async (name) => {
    const next = enabled.includes(name) ? enabled.filter(t => t !== name) : [...enabled, name];
    await saveToolsets(next);
  };

  return (
    <Section title="Internal toolsets">
      <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8 }}>
        Platform toolsets the LLM can invoke. Auto-injected toolsets (analytics, builder, tester) are managed by agent flags.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {KNOWN_TOOLSETS.map((t) => {
          const isOn = enabled.includes(t.name);
          return (
            <button
              key={t.name}
              type="button"
              disabled={!canEdit}
              onClick={() => toggle(t.name)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 12px", borderRadius: 8,
                background: isOn ? `${COLORS.accent}15` : "transparent",
                border: `1px solid ${isOn ? COLORS.accent : COLORS.border}`,
                cursor: canEdit ? "pointer" : "default",
                textAlign: "left",
              }}
            >
              <div style={{
                width: 14, height: 14, borderRadius: 4,
                border: `1.5px solid ${isOn ? COLORS.accent : COLORS.border}`,
                background: isOn ? COLORS.accent : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, color: "#fff",
              }}>{isOn ? "✓" : ""}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 600 }}>{t.name}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim }}>{t.hint}</div>
              </div>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

// ── input/output guardrails (B7) ────────────────────────────
function GuardrailFields({ which, fc, canEdit, saveGuardField }) {
  const gcfg = fc[which] || {};
  return (
    <Section title={which === "input_guardrails" ? "Input guardrails" : "Output guardrails"}>
      <EditableField
        label="Enabled"
        variant="pill"
        value={gcfg.enabled === false ? "no" : "yes"}
        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
        onSave={(v) => saveGuardField(which, "enabled", v === "yes")}
        readOnly={!canEdit}
      />
      {which === "input_guardrails" && (
        <>
          <EditableField
            label="PII detection"
            variant="pill"
            value={gcfg.pii_detection ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveGuardField(which, "pii_detection", v === "yes")}
            readOnly={!canEdit}
          />
          <EditableField
            label="Prompt injection"
            variant="pill"
            value={gcfg.prompt_injection ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveGuardField(which, "prompt_injection", v === "yes")}
            readOnly={!canEdit}
          />
        </>
      )}
      {which === "output_guardrails" && (
        <>
          <EditableField
            label="Hallucination check"
            variant="pill"
            value={gcfg.hallucination_check ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveGuardField(which, "hallucination_check", v === "yes")}
            readOnly={!canEdit}
          />
          <EditableField
            label="Compliance check"
            variant="pill"
            value={gcfg.compliance_check ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveGuardField(which, "compliance_check", v === "yes")}
            readOnly={!canEdit}
          />
        </>
      )}
      <EditableField
        label="Blocked topics (comma-separated)"
        variant="text"
        value={(gcfg.blocked_topics || []).join(", ")}
        onSave={(v) => saveGuardField(which, "blocked_topics",
          (v || "").split(",").map(s => s.trim()).filter(Boolean))}
        readOnly={!canEdit}
      />
    </Section>
  );
}

// ── parallel_prepare → memory + RAG (B8) ────────────────────
function MemoryRagFields({ fc, canEdit, saveMemory, saveKnowledge }) {
  const mem = fc.memory || {};
  const kb = fc.knowledge || {};
  return (
    <>
      <Section title="Memory">
        <EditableField
          label="Long-term memory"
          variant="pill"
          value={mem.long_term_enabled === false ? "no" : "yes"}
          options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
          onSave={(v) => saveMemory("long_term_enabled", v === "yes")}
          readOnly={!canEdit}
          hint="Customer-profile memories injected into the prompt"
        />
        <EditableField
          label="Conversation window"
          variant="number"
          value={mem.window_size ?? 10}
          min={1} max={100}
          onSave={(v) => saveMemory("window_size", Number(v))}
          readOnly={!canEdit}
          hint="How many recent messages to include in context"
        />
      </Section>
      <Section title="RAG defaults">
        <EditableField
          label="top_k"
          variant="slider"
          value={kb.top_k ?? 8}
          min={1} max={50} step={1}
          onSave={(v) => saveKnowledge("top_k", Number(v))}
          readOnly={!canEdit}
        />
        <EditableField
          label="Score threshold"
          variant="slider"
          value={kb.score_threshold ?? 0.30}
          min={0} max={1} step={0.05}
          onSave={(v) => saveKnowledge("score_threshold", Number(v))}
          readOnly={!canEdit}
        />
      </Section>
    </>
  );
}

// ── shared section wrapper ──────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{
      padding: 14, borderRadius: 10,
      border: `1px solid ${COLORS.border}`,
      background: COLORS.surface,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.04em",
        marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

// Read-only fallback for nodes without a dedicated editor.
function ReadOnlyConfigSlice({ node, fc, meta }) {
  const slice = node._configSlice || node?.payload?.config_slice || {};
  return (
    <Section title="Config snapshot (read-only)">
      <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8 }}>
        This node's config doesn't have a dedicated editor yet. Use Builder to make structural changes.
      </div>
      <pre style={{
        margin: 0, padding: 10, borderRadius: 6,
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        color: COLORS.text, fontSize: 10, fontFamily: "monospace",
        maxHeight: 280, overflow: "auto", whiteSpace: "pre-wrap",
      }}>{JSON.stringify(slice, null, 2)}</pre>
    </Section>
  );
}
